import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminDetailFieldLabel,
  AdminFormFieldError,
  AdminFormDropdownChevron,
  AdminTableBody,
  AdminTableHeader,
  AdminCompactFormPage,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminUploadFieldClasses,
  DASHBOARD_DETAIL_FIELD_WIDTH,
} from '../shared/adminUx';
import {
  LBT_ALL_TRADES_CATEGORY_ID,
  LBT_ITEM_TYPE_OPTIONS,
  buildLbtSourceCatalog,
  cloneLbtSourceItem,
  emptyLbtSourceItem,
  getLbtItemTypeLabel,
  resolveDefaultFormIdForLbtType,
} from '../../../data/licensingCredentialRecords';
import {
  LBT_SOURCE_ROUTE,
  SAVE_MESSAGES,
  VALIDATION_MESSAGES,
  collectLbtSourceFieldErrors,
  mergeLbtSourceAfterSave,
  sourceItemMatchesUrlId,
} from '../../../data/licensingSourceWorkflow';
import { buildSampleFormInventory, getLbtFormsForItemType, buildLbtFormInventoryForms } from '../../../data/formInventoryConfig';
import { getFormById } from '../../../data/formInventoryConfigurationConfig';
import { buildSavedTradeCategoryRecords, getFetchedFormFields } from '../../../data/complianceConfiguration';
import { isIconFileSupported } from '../../../data/tradeCategoryWorkflow';

function StackedField({ label, required, error, children }) {
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label}
        {required ? <span className="text-alertred"> *</span> : null}
      </AdminDetailFieldLabel>
      {children}
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
}

function ReadonlyValue({ value, mono = false }) {
  const display = value || '—';
  return (
    <input
      type="text"
      value={display}
      readOnly
      className={`${adminFormDetailInputClasses(true)}${mono ? ' font-mono' : ''}`}
    />
  );
}

function CategoryIconUpload({ value, fileName, error, onChange }) {
  const inputRef = useRef(null);
  const hasFile = Boolean(fileName || value);

  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        Icon <span className="text-alertred">*</span>
      </AdminDetailFieldLabel>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={`${adminUploadFieldClasses()} ${DASHBOARD_DETAIL_FIELD_WIDTH} !bg-white !min-h-[calc(var(--dashboard-field-medium-height)*3)] !h-[calc(var(--dashboard-field-medium-height)*3)] !py-2 hover:border-primaryblue flex flex-col items-center justify-center gap-1 text-center cursor-pointer ${
          error ? '!border-alertred' : ''
        }`}
      >
        {value ? (
          <img src={value} alt="" className="w-8 h-8 object-contain" />
        ) : (
          <i className="fi fi-rs-picture text-base text-mediumgray" aria-hidden />
        )}
        <span className={`text-sm ${hasFile ? 'text-offblack' : 'text-primaryblue'}`}>
          {hasFile ? fileName || 'Icon uploaded' : 'Upload icon'}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          if (!isIconFileSupported(file)) {
            onChange({
              iconUrl: '',
              iconFileName: '',
              iconLabel: '',
              iconError: VALIDATION_MESSAGES.iconUnsupported,
            });
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            onChange({
              iconUrl: reader.result,
              iconFileName: file.name,
              iconLabel: file.name,
              iconError: '',
            });
          };
          reader.readAsDataURL(file);
        }}
      />
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
}

const ComplianceItemDetail = () => {
  const navigate = useNavigate();
  const { recordId: recordIdParam } = useParams();
  const location = useLocation();

  const isAdd = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');

  useEffect(() => {
    if (!isAdd && !isEdit && recordIdParam) {
      navigate(`${LBT_SOURCE_ROUTE}/${encodeURIComponent(recordIdParam)}/edit`, {
        replace: true,
        state: location.state,
      });
    }
  }, [isAdd, isEdit, recordIdParam, navigate, location.state]);

  const forms = useMemo(() => {
    const inventory = Array.isArray(location.state?.forms)
      ? location.state.forms
      : buildSampleFormInventory();
    return [...inventory, ...buildLbtFormInventoryForms()];
  }, [location.state?.forms]);

  const tradeCategories = useMemo(
    () => location.state?.tradeCategories ?? buildSavedTradeCategoryRecords(),
    [location.state?.tradeCategories],
  );

  const tradeCategoryOptions = useMemo(
    () => [
      { value: LBT_ALL_TRADES_CATEGORY_ID, label: 'All Trades' },
      ...tradeCategories.map((row) => ({ value: row.id, label: row.name })),
    ],
    [tradeCategories],
  );

  const allItems = useMemo(
    () => location.state?.lbtSourceItems ?? buildLbtSourceCatalog(forms, tradeCategories),
    [location.state?.lbtSourceItems, forms, tradeCategories],
  );

  const listItem = useMemo(() => {
    if (isAdd) return null;
    const fromState = location.state?.lbtSourceItem;
    if (fromState && sourceItemMatchesUrlId(fromState.id, recordIdParam)) return fromState;
    return allItems.find((row) => sourceItemMatchesUrlId(row.id, recordIdParam)) || null;
  }, [isAdd, recordIdParam, location.state?.lbtSourceItem, allItems]);

  const [formData, setFormData] = useState(emptyLbtSourceItem);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState('');
  const saveLockRef = useRef(false);

  useEffect(() => {
    if (isAdd) {
      setFormData(emptyLbtSourceItem());
      return;
    }
    setFormData(cloneLbtSourceItem(listItem || emptyLbtSourceItem()));
  }, [isAdd, listItem]);

  const setField = (field, value) => {
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    setSaveMessage('');
    setFormData((prev) => {
      if (field === 'itemType') {
        return {
          ...prev,
          itemType: value,
          formId: resolveDefaultFormIdForLbtType(value, forms),
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const pageHeading = isAdd ? 'Add New' : 'Edit';

  const navigateToList = (nextItems, flashMessage) => {
    navigate(LBT_SOURCE_ROUTE, {
      state: {
        lbtSourceItems: nextItems,
        forms,
        tradeCategories,
        ...(flashMessage ? { flashMessage } : {}),
      },
    });
  };

  const handleCancel = () => {
    navigateToList(allItems);
  };

  const handleIconChange = ({ iconUrl, iconFileName, iconLabel, iconError }) => {
    setSaveMessage('');
    setFormData((prev) => ({
      ...prev,
      iconUrl,
      iconFileName,
      iconLabel: iconLabel || iconFileName || prev.iconLabel,
    }));
    setFieldErrors((prev) => ({
      ...prev,
      icon: iconError || undefined,
    }));
  };

  const handleSave = () => {
    if (saveLockRef.current) return;
    const errors = collectLbtSourceFieldErrors(formData, {
      items: allItems,
      isEdit: isEdit && Boolean(listItem?.id),
      tradeCategories,
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSaveMessage(SAVE_MESSAGES.failure);
      return;
    }
    saveLockRef.current = true;
    const nextList = mergeLbtSourceAfterSave(allItems, formData, {
      isNew: isAdd,
      forms,
    });
    navigateToList(nextList, SAVE_MESSAGES.success);
  };

  const availableForms = useMemo(() => {
    const typed = getLbtFormsForItemType(forms, formData.itemType);
    const selected = getFormById(forms, formData.formId);
    if (selected && !typed.some((form) => form.id === selected.id)) {
      return [selected, ...typed];
    }
    return typed;
  }, [forms, formData.itemType, formData.formId]);
  const inventoryFields = useMemo(
    () => getFetchedFormFields(getFormById(forms, formData.formId)),
    [forms, formData.formId],
  );

  if (!isAdd && !isEdit) {
    return (
      <AdminLayout>
        <AdminPageTitle>Redirecting…</AdminPageTitle>
      </AdminLayout>
    );
  }

  if (!isAdd && !listItem) {
    return (
      <AdminLayout>
        <AdminPageTitle>Record not found</AdminPageTitle>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={pageHeading}
        onSubmit={handleSave}
        showSave={false}
        beforeForm={
          saveMessage ? (
            <div className="mb-6">
              <AdminFormFieldError className="mb-0">{saveMessage}</AdminFormFieldError>
            </div>
          ) : null
        }
      >
        <StackedField label="ID">
          <ReadonlyValue value={isAdd ? '' : formData.id} mono />
        </StackedField>

        <StackedField label="Type" required={isAdd} error={fieldErrors.itemType}>
          {!isAdd ? (
            <ReadonlyValue value={getLbtItemTypeLabel(formData.itemType)} />
          ) : (
            <div className="relative w-full min-w-0">
              <select
                value={formData.itemType || ''}
                onChange={(e) => setField('itemType', e.target.value)}
                className={adminFormDetailSelectClasses(false, Boolean(fieldErrors.itemType))}
                aria-label="Type"
              >
                <option value="">Select Type</option>
                {LBT_ITEM_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
          )}
        </StackedField>

        <StackedField label="Name" required error={fieldErrors.itemName}>
          <input
            type="text"
            value={formData.itemName || ''}
            onChange={(e) => setField('itemName', e.target.value)}
            className={adminFormDetailInputClasses(false, Boolean(fieldErrors.itemName))}
          />
        </StackedField>

        <StackedField label="Description" required error={fieldErrors.description}>
          <textarea
            value={formData.description || ''}
            onChange={(e) => setField('description', e.target.value)}
            rows={4}
            className={`${adminFormDetailTextareaClasses(false, Boolean(fieldErrors.description))} min-h-[100px]`}
          />
        </StackedField>

        <StackedField label="Trade Category" required error={fieldErrors.tradeCategoryId}>
          <div className="relative w-full min-w-0">
            <select
              value={formData.tradeCategoryId || ''}
              onChange={(e) => setField('tradeCategoryId', e.target.value)}
              className={adminFormDetailSelectClasses(false, Boolean(fieldErrors.tradeCategoryId))}
              aria-label="Trade Category"
            >
              <option value="">Select Trade Category</option>
              {tradeCategoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <AdminFormDropdownChevron />
          </div>
        </StackedField>

        <CategoryIconUpload
          value={formData.iconUrl}
          fileName={formData.iconFileName}
          error={fieldErrors.icon}
          onChange={handleIconChange}
        />

        <StackedField label="Form" required error={fieldErrors.formId}>
          <div className="relative w-full min-w-0">
            <select
              value={formData.formId || ''}
              onChange={(e) => setField('formId', e.target.value)}
              className={adminFormDetailSelectClasses(false, Boolean(fieldErrors.formId))}
              aria-label="Form"
              disabled={!formData.itemType}
            >
              <option value="">Select Form</option>
              {availableForms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.formName}
                </option>
              ))}
            </select>
            <AdminFormDropdownChevron />
          </div>
        </StackedField>

        {inventoryFields.length > 0 ? (
          <div className="space-y-4">
            <AdminPanelTitle className="text-offblack !mb-0">Form Definition</AdminPanelTitle>
            <div className="overflow-x-auto min-w-0">
              <div
                className="grid w-full py-3 text-offblack border-b border-lightgray gap-x-3"
                style={{ gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 0.8fr)' }}
              >
                <AdminTableHeader>Field</AdminTableHeader>
                <AdminTableHeader>Type</AdminTableHeader>
                <AdminTableHeader>Requirement</AdminTableHeader>
              </div>
              {inventoryFields.map((field) => (
                <div
                  key={field.id || field.fieldLabel}
                  className="grid w-full py-3 text-offblack border-b border-lightgray last:border-b-0 gap-x-3"
                  style={{ gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 0.8fr)' }}
                >
                  <AdminTableBody className="break-words min-w-0">
                    {field.fieldLabel || '—'}
                  </AdminTableBody>
                  <AdminTableBody className="truncate min-w-0">
                    {field.controlType || '—'}
                  </AdminTableBody>
                  <AdminTableBody className="truncate min-w-0">
                    {field.baseRequirement === 'Required' ? 'Required' : 'Optional'}
                  </AdminTableBody>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <AdminFormSecondaryAction type="button" onClick={handleCancel}>
            Cancel
          </AdminFormSecondaryAction>
          <AdminHeaderAction type="submit">Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default ComplianceItemDetail;
